import * as React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    Divider,
} from '@mui/material';
import useLocales from '../../../hooks/useLocales';
import { fuelTypeLabel } from '../../../utils/typeLabels';
import {
    FT_OP_FILL,
    FT_OP_REFILL,
    FT_OP_ADJUST,
    FT_OP_GAS,
    FT_OP_EV,
    FT_EV_PRIVATE,
    operationTypeLabelKey,
    paymentLabelKey,
    evLocationLabelKey,
    fmtQty,
} from '../list/FTRecentOperationsTable';

const fmtDate = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString() : '');
const fmtDateTime = (ms) => (ms ? new Date(Number(ms)).toLocaleString() : '');
const num = (v) => (v == null || v === '' ? '-' : String(Math.round(Number(v))));
const money = (v) => (v == null || v === '' ? '-' : numeral(Number(v)).format('0,0.00'));

const Row = ({ label, value, always = false }) => {
    if ((value === undefined || value === null || value === '' || value === '-') && !always) return null;
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                {value === undefined || value === null || value === '' ? '-' : value}
            </Typography>
        </Stack>
    );
};

Row.propTypes = {
    label: PropTypes.string,
    value: PropTypes.any,
    always: PropTypes.bool,
};

FTOperationDetailModal.propTypes = {
    passedData: PropTypes.object,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
};

export default function FTOperationDetailModal({ passedData, open, handleClose }) {
    const { translate } = useLocales();
    const op = passedData || {};
    const type = Number(op.operationTypeId);
    const isExternal = type === FT_OP_GAS || type === FT_OP_EV;
    // Private charger price/kWh is not stored - derive it from cost / energy.
    const evPrice =
        Number(op.evLocation) === FT_EV_PRIVATE && op.totalCost != null && Number(op.energyKwh) > 0
            ? fmtQty(Number(op.totalCost) / Number(op.energyKwh))
            : null;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" aria-labelledby="ft-operation-detail">
            <DialogTitle id="ft-operation-detail">
                {translate(operationTypeLabelKey(type))}
            </DialogTitle>
            <DialogContent dividers>
                {!isExternal && <Row label={translate('fuelTank')} value={op.fuelTankName} always />}
                {isExternal && <Row label={translate('fuelType')} value={fuelTypeLabel(translate, op.fuelTypeId, op.fuelTypeName)} always />}
                <Row label={translate('date')} value={fmtDate(op.date)} always />

                {type === FT_OP_FILL && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Row label={translate('vehicle')} value={op.machineName} always />
                        <Row label={translate('tankReadingBefore')} value={num(op.counterBefore)} always />
                        <Row label={translate('tankReadingAfter')} value={num(op.counterAfter)} always />
                        <Row label={translate('litresUsed')} value={fmtQty(op.volume)} always />
                        <Row label={translate('vehicleReading')} value={num(op.machineCounter)} />
                    </>
                )}

                {type === FT_OP_REFILL && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Row label={translate('volume')} value={fmtQty(op.volume)} always />
                        <Row label={translate('supplier')} value={op.supplier} always />
                        <Row label={translate('guideNumber')} value={op.guideNumber} always />
                        <Row label={translate('newLevel')} value={fmtQty(op.newLitres)} />
                    </>
                )}

                {type === FT_OP_ADJUST && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Row label={translate('newLevel')} value={fmtQty(op.newLitres)} always />
                        <Row label={translate('newReading')} value={num(op.newCounter)} always />
                    </>
                )}

                {type === FT_OP_GAS && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        {/* Same order as the app fill form: where -> how we pay -> how much -> counter. */}
                        <Row label={translate('vehicle')} value={op.machineName} always />
                        <Row label={translate('station')} value={op.stationName} />
                        <Row label={translate('payment')} value={op.paymentMethod ? translate(paymentLabelKey(op.paymentMethod)) : '-'} />
                        <Row label={translate('litres')} value={fmtQty(op.volume)} always />
                        <Row label={translate('totalPaid')} value={money(op.totalCost)} always />
                        <Row label={translate('vehicleReading')} value={num(op.machineCounter)} />
                        <Row label={translate('fullTank')} value={op.fullTank ? translate('yes') : translate('no')} />
                    </>
                )}

                {type === FT_OP_EV && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Row label={translate('vehicle')} value={op.machineName} always />
                        <Row label={translate('evWhere')} value={op.evLocation ? translate(evLocationLabelKey(op.evLocation)) : '-'} always />
                        {/* Public-only: station + payment. Private-only: derived price/kWh. (empty rows hide) */}
                        <Row label={translate('station')} value={op.stationName} />
                        <Row label={translate('payment')} value={op.paymentMethod ? translate(paymentLabelKey(op.paymentMethod)) : '-'} />
                        <Row label={translate('pricePerKwh')} value={evPrice} />
                        <Row label={translate('energyKwh')} value={fmtQty(op.energyKwh)} always />
                        <Row label={translate('totalPaid')} value={money(op.totalCost)} />
                        <Row label={translate('batteryBefore')} value={num(op.batteryBefore)} />
                        <Row label={translate('batteryAfter')} value={num(op.batteryAfter)} />
                        <Row label={translate('vehicleReading')} value={num(op.machineCounter)} />
                    </>
                )}

                <Divider sx={{ my: 1 }} />
                <Row label={translate('notes')} value={op.notes} always />
                <Row label={translate('user')} value={op.username} always />
                <Row label={translate('registeredAt')} value={fmtDateTime(op.createdAt)} />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={handleClose}>
                    {translate('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
